<template>
  <div>
    <div>
      <section
        v-if="item.template.configFields
          && item.nonSectionFields.length"
        :class="$style.sectionRoot"
      >
        <header :class="$style.sectionHeader">
          Settings
        </header>
        <div :class="$style.sectionContent">
          <div
            v-if="item.template.alert"
            :class="$style.alertContainer"
          >
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" :class="$style.infoIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#4373c7" fill-rule="evenodd" d="M6.9 3.333c-.544 0-.9-.36-.9-.917a1.1 1.1 0 011.088-1.083c.524 0 .912.367.912.924 0 .62-.506 1.076-1.1 1.076m-2.037 6c-.408 0-.691-.215-.691-.698 0-.614.385-1.657.466-1.872L5.43 4.52c.08-.232-.022-.346-.113-.346-.418 0-.964 1.11-1.168 1.11-.08 0-.15-.09-.15-.167 0-.227.504-.764.648-.93.456-.484 1.045-.854 1.7-.854.487 0 1.012.334.6 1.569l-.815 2.481c-.07.191-.193.513-.193.716 0 .09.048.179.14.179.337 0 .958-1.086 1.12-1.086.058 0 .133.078.133.191 0 .37-1.323 1.95-2.47 1.95m6.332-6.345A5.973 5.973 0 009.012.805 5.868 5.868 0 006 0a5.868 5.868 0 00-3.012.805A5.973 5.973 0 00.805 2.988 5.871 5.871 0 000 6a5.87 5.87 0 00.805 3.012 5.974 5.974 0 002.183 2.184A5.87 5.87 0 006 12a5.87 5.87 0 003.012-.804 5.974 5.974 0 002.183-2.184C11.732 8.092 12 7.089 12 6a5.869 5.869 0 00-.805-3.012"/></svg>
            </div>
            <div :class="$style.alertContent">
              <p>{{ item.template.alert }}</p>
              <p v-if="item.template.reference">
                For more information, please reference our <a
                  :href="item.template.reference"
                  rel="noopener noreferrer"
                  target="_blank"
                >KB Article.</a>
              </p>
            </div>
          </div>
          <DynamicControls
            ref="list"
            :configFields="!!item.sections.length ?
              item.nonSectionFields : item.template.configFields"
            :configValues="item.values"
            :handleValueChange="handleValueChange"
          />
        </div>
      </section>
      <CollapsableSection
        v-for="(section, sectionIndex) in item.sections"
        :key="sectionIndex"
        :title="section.title"
      >
        <MacOspPolicyCustomNotificationSettings
          v-if="isMacOspPolicy && section.id === previewNotificationSection"
          :configFields="item.template.configFields"
          :configValues="item.values"
          :handleValueChange="handleValueChange"
        />
        <DynamicControls
          v-else
          :ref="`list-${sectionIndex}`"
          :configFields="item.template.configFields"
          :configValues="item.values"
          :handleValueChange="handleValueChange"
          :sectionId="section.id"
        />
      </CollapsableSection>
    </div>
  </div>
</template>
<script>
import {
  ItemTypeConsts,
  StoreModelMap,
} from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import { mapActions } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CollapsableSection from 'client/components/ContentSection/CollapsableSection.vue';
import DynamicControls from 'client/components/DynamicControls/DynamicControls.vue';
import MacOspPolicyCustomNotificationSettings from 'client/policies/MacOspPolicyCustomNotificationSettings';
import configurationCategoriesConst from 'client/util/Constants/ConfigurationCategoriesConstants';

const {
  configuration,
} = ItemTypeConsts;
const configurationModel = StoreModelMap[configuration];
const previewNotificationSection = 'NOTIFICATION_SETTINGS';

export default {
  name: 'ConfigurationSettings',

  inject: ['$validator'],

  components: {
    CollapsableSection,
    DynamicControls,
    MacOspPolicyCustomNotificationSettings,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  data: () => (
    {
      previewNotificationSection,
    }
  ),

  computed: {
    isMacOspPolicy() {
      return this.item.template.name
        === configurationCategoriesConst.darwin.automaticMacOsUpdates.name;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  created() {
    this.addOsPatchManagementValidators();
    this.addBrowserPatchManagementValidators();
  },

  methods: {
    ...mapActions(configurationModel, [
      'updateItem',
    ]),

    /*
      This method is only for Browser Patch Management policies
    */
    addBrowserPatchManagementValidators() {
      const chromeConfigFieldNames = [
        'RelaunchNotificationPeriod',
      ];
      const itemObject = cloneDeep(this.item);
      let templatesLength = 0;

      chromeConfigFieldNames.forEach((configFieldName, index) => {
        const template = itemObject.template.configFields.find(
          (configField) => (configField.name === configFieldName),
        );
        if (template) {
          template.label = `${template.label}--${index}`;
          template.validators = {
            min_value: 3600000,
            integer: true,
            required: 'required',
          };
          templatesLength += 1;
        }
      });

      if (templatesLength > 0) {
        this.updateItem(itemObject);
      }
    },

    /*
      This method is only for OS Patch Management policies
      Note: We will remove this method once BE return the validators on API response.
    */
    addOsPatchManagementValidators() {
      const configFieldNames = [
        'MajorUpdateDelayDays',
        'MinorUpdateDeferralDays',
        'MinorUpdateInstallGracePeriod',
        'NonOSUpdateDelayDays',
      ];
      const itemObject = cloneDeep(this.item);
      let templatesLength = 0;

      configFieldNames.forEach((configFieldName, index) => {
        const template = itemObject.template.configFields.find(
          (configField) => (configField.name === configFieldName),
        );
        if (template) {
          template.label = `${template.label}--${index}`;
          template.validators = {
            max_value: 90,
            // Minor OS Update Installation Grace Period should allow 0 to 90 days
            min_value: template.name === 'MinorUpdateInstallGracePeriod' ? 0 : 1,
            integer: true,
            required: 'required',
          };
          templatesLength += 1;
        }
      });

      if (templatesLength > 0) {
        this.updateItem(itemObject);
      }
    },

    async handleValueChange(controlId, value) {
      const newValues = this.item.values.map((configValue) => {
        const currentValue = cloneDeep(configValue);

        if (configValue.configFieldID === controlId) {
          currentValue.value = value;
        }
        return currentValue;
      });

      if (!newValues.find(newValue => newValue.configFieldID === controlId)) {
        newValues.push({
          configFieldID: controlId,
          value,
        });
      }
      const values = { key: 'values', value: newValues };
      this.setItemField(values);
    },
  },
};

</script>
<style module>
@import '../../css/sections.css';
@import '../../components/FormValidation/css/formFields.css';

.alertContainer {
  background-color: var(--jcInformational50);
  border: 1px solid var(--jc-color-informational-base);
  display: flex;
  margin-bottom: 10px;
  padding: 10px 10px 0;
}

.alertContent {
  padding-left: 10px;
}

.infoIcon {
  height: 15px;
}
</style>
